import React, { useMemo, useState } from 'react';
import { PortalButton, PortalModal, PortalModalSpacer, PortalTextArea } from '../ui';
import { usePortalCopy, usePortalUi } from '../PortalUi';
import { usePortalQuery } from '../hooks/usePortalQuery';
import {
  isPortalHttpError,
  proposeRequirementAvailability,
  type PortalRequirement,
} from '../api/portalService';
import { ScheduleDayField } from './ScheduleDayField';
import { isWeekendDay } from './mxHolidays';
import {
  MAX_AVAILABILITY_OPTIONS,
  MAX_NOTE_LENGTH,
  appointmentTypeFor,
  availabilitySlotHoursFor,
  earliestAvailabilityDay,
  isoFromLocalSlot,
  latestAvailabilityDay,
  localSlotFromIso,
} from './scheduleSlots';

/**
 * OUW-260: el cliente propone horarios para la videollamada con el equipo de
 * suscripción.
 *
 * Propone, no agenda: el analista elige una de las opciones y manda el invite.
 * Por eso el modal no promete una cita ni muestra confirmación de horario.
 *
 * La reunión dura una hora y eso NO se elige: cada opción es un día más una
 * hora de comienzo. Pedirle al cliente que arme un rango sería pedirle que
 * decida algo que ya está decidido.
 *
 * OUW-336: el examen médico con Olé reusa el modal con su título y bajada, sin
 * el encabezado de la videollamada, y con sus propias reglas de día y hora
 * (`scheduleSlots.ts`). Las horas de cada cita no cambian.
 *
 * El día, en las dos citas, sale de `ScheduleDayField`: lunes a viernes.
 * El `<input type="date">` nativo no puede apagar el fin de semana, y esa
 * regla no se aplica a ningún otro calendario de la app.
 */
export function ScheduleModal() {
  const { liveRequirement } = usePortalUi();
  if (!liveRequirement) return null;
  return <LiveScheduleModal requirement={liveRequirement} />;
}

type Slot = { day: string; hour: string };

const EMPTY_SLOT: Slot = { day: '', hour: '' };

function LiveScheduleModal({ requirement }: { requirement: PortalRequirement }) {
  const t = usePortalCopy();
  const { quoteID } = usePortalQuery();
  const { closeModal, requirementRefetch } = usePortalUi();
  const type = appointmentTypeFor(requirement);
  const exam = type === 'medical_exam';
  // Las horas se piden con la hora de cada render (el margen de 12 h corre).
  const hoursFor = (day: string) => availabilitySlotHoursFor(day, new Date(), type);

  // Se arranca con lo ya propuesto: el cliente que vuelve a abrir corrige,
  // no empieza de cero. Una fila sola cuando todavía no propuso nada —
  // pedir tres de entrada parece un formulario.
  const [slots, setSlots] = useState<Slot[]>(() => {
    const previous = requirement.availability?.options ?? [];
    if (previous.length === 0) return [EMPTY_SLOT];
    const restored = previous.map(localSlotFromIso);
    // Fin de semana: ya no se puede reenviar. Examen: una propuesta de antes de
    // OUW-336 puede quedar antes del mínimo o fuera del horario de laboratorio.
    // Se le borra la hora para que el cliente la vea vacía y la vuelva a elegir.
    return restored.map((slot) => {
      if (isWeekendDay(slot.day)) return { ...slot, hour: '' };
      if (exam && !hoursFor(slot.day).includes(slot.hour)) return { ...slot, hour: '' };
      return slot;
    });
  });
  const [note, setNote] = useState(requirement.availability?.note ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const minDay = useMemo(() => earliestAvailabilityDay(new Date(), type), [type]);
  const maxDay = useMemo(() => latestAvailabilityDay(new Date(), type), [type]);

  // Examen: solo cuentan las filas que cumplen la regla (red de seguridad para
  // una fecha tipeada antes del mínimo).
  const filled = slots.filter((slot) => {
    if (!slot.day || !slot.hour || isWeekendDay(slot.day)) return false;
    if (exam && !hoursFor(slot.day).includes(slot.hour)) return false;
    return true;
  });
  const canSend = !busy && filled.length > 0;

  const setSlot = (index: number, patch: Partial<Slot>) => {
    setError(null);
    setSlots((current) => current.map((slot, i) => (i === index ? { ...slot, ...patch } : slot)));
  };

  const addSlot = () => {
    setError(null);
    setSlots((current) =>
      current.length >= MAX_AVAILABILITY_OPTIONS ? current : [...current, EMPTY_SLOT],
    );
  };

  const removeSlot = (index: number) => {
    setError(null);
    setSlots((current) => (current.length <= 1 ? current : current.filter((_, i) => i !== index)));
  };

  const send = async () => {
    const options = filled.map((slot) => isoFromLocalSlot(slot.day, slot.hour));
    // Dos filas con el mismo horario son un descuido de carga, no un error
    // del servidor: se avisa acá antes de gastar el viaje.
    if (new Set(options).size !== options.length) {
      setError(t('scheduleErrorRepeated'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await proposeRequirementAvailability(quoteID, requirement.id, options, note);
      setDone(true);
      requirementRefetch?.();
    } catch (err) {
      if (isPortalHttpError(err) && err.status === 409) {
        // El analista cerró el requisito mientras el modal estaba abierto.
        closeModal();
        requirementRefetch?.();
        return;
      }
      setError(
        isPortalHttpError(err) && err.status === 400
          ? t('scheduleErrorInvalid')
          : t('scheduleErrorSend'),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <PortalModal
      id="m-schedule"
      onClose={closeModal}
      closeLabel={t('close')}
      footer={
        done ? (
          <>
            <PortalModalSpacer />
            <PortalButton label={t('done')} data-spec="o-schedule-done" onClick={closeModal} />
          </>
        ) : (
          <>
            <PortalModalSpacer />
            <PortalButton variant="ghost" label={t('cancel')} onClick={closeModal} />
            <PortalButton
              label={busy ? t('scheduleSending') : t('scheduleSend')}
              data-spec="o-schedule-send"
              data-wired
              disabled={!canSend}
              onClick={() => { void send(); }}
            />
          </>
        )
      }
    >
      {done ? (
        <>
          {exam ? null : <p className="eyebrow">{t('scheduleEyebrow')}</p>}
          <h2>{t('scheduleDoneTitle')}</h2>
          <p>{t('scheduleDoneLead')}</p>
        </>
      ) : (
        <>
          {exam ? null : <p className="eyebrow">{t('scheduleEyebrow')}</p>}
          <h2>{t(exam ? 'scheduleExamTitle' : 'scheduleTitle')}</h2>
          <p>{t(exam ? 'scheduleExamLead' : 'scheduleLead')}</p>

          <div className="sched-slots">
            {slots.map((slot, index) => (
              // El índice alcanza como clave: las filas no se reordenan y se
              // identifican por su posición en la lista.
              // eslint-disable-next-line react/no-array-index-key
              <div className="sched-slot" key={index}>
                <div className="sched-field">
                  <span>
                    <label htmlFor={`schedule-day-${index}`}>{t('scheduleDay')}</label>
                  </span>
                  <ScheduleDayField
                    id={`schedule-day-${index}`}
                    value={slot.day}
                    min={minDay}
                    max={maxDay}
                    disabled={busy}
                    dataSpec={`o-schedule-day-${index}`}
                    onChange={(day) => {
                      // Si la hora elegida queda dentro del margen para el día
                      // nuevo, se limpia: dejarla sería ofrecer un 400.
                      const stillValid = hoursFor(day).includes(slot.hour);
                      setSlot(index, { day, hour: stillValid ? slot.hour : '' });
                    }}
                  />
                </div>
                <label className="sched-field">
                  <span>{t('scheduleHour')}</span>
                  <select
                    className="inp"
                    value={slot.hour}
                    disabled={busy}
                    data-spec={`o-schedule-hour-${index}`}
                    onChange={(event) => setSlot(index, { hour: event.target.value })}
                  >
                    <option value="">{t('scheduleHourPlaceholder')}</option>
                    {hoursFor(slot.day).map((hour) => (
                      <option key={hour} value={hour}>{hour}</option>
                    ))}
                  </select>
                </label>
                {slots.length > 1 ? (
                  <button
                    type="button"
                    className="sched-remove"
                    aria-label={t('scheduleRemove')}
                    disabled={busy}
                    data-spec={`o-schedule-remove-${index}`}
                    onClick={() => removeSlot(index)}
                  >
                    ✕
                  </button>
                ) : null}
              </div>
            ))}
          </div>

          {slots.length < MAX_AVAILABILITY_OPTIONS ? (
            <button
              type="button"
              className="attach sched-add"
              disabled={busy}
              data-spec="o-schedule-add"
              onClick={addSlot}
            >
              {t('scheduleAdd')}
            </button>
          ) : null}

          <PortalTextArea
            placeholder={t('scheduleNotePlaceholder')}
            maxLength={MAX_NOTE_LENGTH}
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />

          {error ? <p className="sched-error" role="alert">{error}</p> : null}
          <p className="foot-note">{t('scheduleFoot')}</p>
        </>
      )}
    </PortalModal>
  );
}
